import { useEffect, useState } from 'react';
import { Search, ChevronDown, Package, Trash2 } from 'lucide-react';
import { useOnboardingStore } from '../store/onboarding-store';
import { mockFunnels, FunnelData, Product } from '../types/index';
import { use } from 'video.js/dist/types/tech/middleware';
import { getSectionsGenerated } from '../../../services/funnels';
import { useWebsitesStore } from '../../../store/websitesStore';
import { getProductDetail } from '../../../services/store/productAxios';
import { dictionary, Language } from '../../../utils/dictionary';
import { useAuth } from '../../../context/AuthContext';
export interface ProductContext {
  product_name: string;
  product_description: string;
}

export interface PayloadBrandContext {
  selected_sale_angle: string;
  products: ProductContext[];
  language: Language;
}

interface StepSelectFunnelsProps {
  setPayloadBrandContext: (payloadBrandContext: PayloadBrandContext[]) => void;
  payloadBrandContext: PayloadBrandContext[];
}

export function StepSelectFunnels({ setPayloadBrandContext, payloadBrandContext }: StepSelectFunnelsProps) {
  const { language } = useAuth();
  const { websites, initialize } = useWebsitesStore();
  const { data, addFunnel, removeFunnel } = useOnboardingStore();
  const [funnelForOnboarding, setFunnelForOnboarding] = useState<FunnelData[]>([]);
  const { selectedFunnels } = data;
  const [searchTerm, setSearchTerm] = useState('');
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [funnelBrandContextMap, setFunnelBrandContextMap] = useState<Map<string, PayloadBrandContext>>(new Map())

  useEffect(() => {
    initialize();
  }, [initialize]);

  useEffect(() => {
    const processFunnels = async () => {
      if (websites.length === 0) return

      setLoading(true)

      try {
        const validWebsites = websites.filter((website) => website.products.length > 0 && website.status !== "BUILDING")
        const funnelResults: FunnelData[] = []
        const brandContextMap = new Map<string, PayloadBrandContext>()

        const websitePromises = validWebsites.map(async (website) => {
          let mainProduct: Product = { name: "", price: "", image: "" }
          let orderBump: Product = { name: "", price: "", image: "" }
          let upsell: Product = { name: "", price: "", image: "" }
          const productsContext: ProductContext[] = []

          const productPromises = website.products.map(async (product) => {
            try {
              const detailProduct: any = await getProductDetail(product.product_id)
              const productData: Product = {
                name: detailProduct?.name || "Sin nombre",
                price: detailProduct?.price || "0",
                image: detailProduct?.base_image_url || detailProduct?.images[0] || "/placeholder.svg?height=100&width=100",
              }

              productsContext.push({
                product_name: detailProduct?.name || "Sin nombre",
                product_description: detailProduct?.description || "Sin descripción",
              })

              return { type: product.type, data: productData }
            } catch (error) {
              console.error(`Error obteniendo producto ${product.product_id}:`, error)
              productsContext.push({
                product_name: "Error al cargar",
                product_description: "No se pudo cargar la descripción",
              })

              return {
                type: product.type,
                data: { name: "Error", price: "0", image: "/placeholder.svg?height=100&width=100" },
              }
            }
          })

          const resolvedProducts = await Promise.all(productPromises)

          resolvedProducts.forEach(({ type, data }) => {
            if (type === "PRINCIPAL") {
              mainProduct = data
            } else if (type === "UP_SELL") {
              upsell = data
            } else if (type === "BUMP_OFFER") {
              orderBump = data
            }
          })

          const funnelResponse: FunnelData = {
            id: website.websiteId,
            number: website.idSequence.toString(),
            name: website.name,
            mainProduct: mainProduct,
            totalProducts: website.products.length,
          }

          if (orderBump.name) {
            funnelResponse.orderBump = orderBump
          }
          if (upsell.name) {
            funnelResponse.upsell = upsell
          }

          const brandContextResponse: PayloadBrandContext = {
            selected_sale_angle: website.sales_angle_description,
            products: productsContext,
            language: language as Language,
          }

          // Guardar en el mapa para uso posterior
          brandContextMap.set(website.websiteId, brandContextResponse)

          return funnelResponse
        })

        const results = await Promise.all(websitePromises)

        setFunnelForOnboarding(results)
        setFunnelBrandContextMap(brandContextMap)
      } catch (error) {
        console.error("Error procesando funnels y brand context:", error)
      } finally {
        setLoading(false)
      }
    }

    processFunnels()
  }, [websites])

  useEffect(() => {
    const updatePayloadBrandContext = () => {
      const selectedBrandContexts: PayloadBrandContext[] = []

      selectedFunnels.forEach((selectedFunnel) => {
        const brandContext = funnelBrandContextMap.get(selectedFunnel.id)
        if (brandContext) {
          selectedBrandContexts.push(brandContext)
        }
      })

      setPayloadBrandContext(selectedBrandContexts)
    }

    updatePayloadBrandContext()
  }, [selectedFunnels, funnelBrandContextMap])

  const filteredFunnels = funnelForOnboarding.filter(funnel =>
    funnel.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    funnel.mainProduct?.name?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const availableFunnels = filteredFunnels.filter(funnel =>
    !selectedFunnels.some(selected => selected.id === funnel.id)
  );

  const handleFunnelSelect = (funnel: FunnelData) => {
    addFunnel(funnel);
    setSearchTerm('');
    setIsDropdownOpen(false);
  };

  return (
    <div className="h-[500px] flex flex-col">
      {/* Header - Fixed */}
      <div className="flex-shrink-0 mb-6">
        <h3 className="text-xl font-medium mb-2">{dictionary["select-funnels-homes"]?.[language as Language] ?? "Selecciona los embudos para tu tienda"}</h3>
      </div>

      {/* Search/Dropdown Bar - Fixed */}
      <div className="flex-shrink-0 mb-6">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
          <input
            type="text"
            className="input pl-10 pr-10"
            placeholder={dictionary["search-funnels"]?.[language as Language] ?? "Buscar y seleccionar embudos..."}
            value={searchTerm}
            onChange={(e) => {
              setSearchTerm(e.target.value);
              setIsDropdownOpen(true);
            }}
            onFocus={() => setIsDropdownOpen(true)}
          />
          <ChevronDown
            className={`absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5 transition-transform ${isDropdownOpen ? 'rotate-180' : ''
              }`}
          />

          {/* Dropdown */}
          {isDropdownOpen && (
            <div className="absolute top-full left-0 right-0 mt-1 bg-white border border-gray-200 rounded-lg shadow-lg z-10 max-h-64 overflow-y-auto">
              {availableFunnels.length > 0 ? (
                availableFunnels.map((funnel) => (
                  <button
                    key={funnel.id}
                    type="button"
                    className="w-full flex items-center space-x-3 p-3 hover:bg-gray-50 border-b border-gray-100 last:border-b-0"
                    onClick={() => handleFunnelSelect(funnel)}
                  >
                    <img
                      src={funnel.mainProduct.image}
                      alt={funnel.mainProduct.name}
                      className="w-12 h-12 rounded-lg object-cover flex-shrink-0"
                    />
                    <div className="flex-1 text-left">
                      <h5 className="font-medium text-gray-900 truncate">
                        <span className="text-gray-500 mr-2">#{funnel.number}</span>
                        {funnel.name}
                      </h5>
                      <p className="text-sm text-gray-600 truncate">
                        {funnel.mainProduct.name}
                      </p>
                      <div className="flex items-center space-x-3 text-xs text-gray-500 mt-1">
                        <div className="flex items-center">
                          <Package className="w-3 h-3 mr-1" />
                          {funnel.totalProducts} productos
                        </div>
                        <span>${funnel.mainProduct.price}</span>
                      </div>
                    </div>
                  </button>
                ))
              ) : (
                <div className="p-4 text-center text-gray-500">
                  {searchTerm ? dictionary["no-funnels-found"]?.[language as Language] ?? "No se encontraron embudos" : dictionary["no-funnels-available"]?.[language as Language] ?? "No hay más embudos disponibles"}
                </div>
              )}
            </div>
          )}
        </div>

        {/* Click outside to close dropdown */}
        {isDropdownOpen && (
          <div
            className="fixed inset-0 z-0"
            onClick={() => setIsDropdownOpen(false)}
          />
        )}
      </div>

      {/* Selected Funnels - Scrollable */}
      <div className="flex-1 overflow-hidden">
        <div className="h-full overflow-y-auto scrollable-container">
          {selectedFunnels.length > 0 ? (
            <div className="space-y-4">
              {selectedFunnels.map((funnel) => (
                <div key={funnel.id} className="bg-white rounded-lg p-4 border border-gray-200 shadow-sm">
                  <div className="flex items-center justify-between mb-3">
                    <h5 className="font-medium text-gray-900 truncate pr-2">
                      <span className="text-gray-500 mr-2">#{funnel.number}</span>
                      {funnel.name}
                    </h5>
                    <div className="flex items-center space-x-2 flex-shrink-0">
                      <button
                        type="button"
                        onClick={() => removeFunnel(funnel.id)}
                        className="p-1 text-gray-400 hover:text-red-600"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </div>

                  <div className="space-y-3">
                    {/* Main Product */}
                    <div className="flex items-center space-x-3">
                      <img
                        src={funnel.mainProduct.image}
                        alt={funnel.mainProduct.name}
                        className="w-12 h-12 rounded-lg object-cover flex-shrink-0"
                      />
                      <div className="flex-1 min-w-0">
                        <p className="text-xs text-gray-500">Producto principal</p>
                        <p className="font-medium truncate">{funnel.mainProduct.name}</p>
                        <p className="text-sm text-gray-600">${funnel.mainProduct.price}</p>
                      </div>
                    </div>

                    {/* Order Bump */}
                    {funnel.orderBump && (
                      <div className="flex items-center space-x-3">
                        <img
                          src={funnel.orderBump.image}
                          alt={funnel.orderBump.name}
                          className="w-12 h-12 rounded-lg object-cover flex-shrink-0"
                        />
                        <div className="flex-1 min-w-0">
                          <p className="text-xs text-gray-500">Order bump</p>
                          <p className="font-medium truncate">{funnel.orderBump.name}</p>
                          <p className="text-sm text-gray-600">${funnel.orderBump.price}</p>
                        </div>
                      </div>
                    )}

                    {/* Upsell */}
                    {funnel.upsell && (
                      <div className="flex items-center space-x-3">
                        <img
                          src={funnel.upsell.image}
                          alt={funnel.upsell.name}
                          className="w-12 h-12 rounded-lg object-cover flex-shrink-0"
                        />
                        <div className="flex-1 min-w-0">
                          <p className="text-xs text-gray-500">Upsell</p>
                          <p className="font-medium truncate">{funnel.upsell.name}</p>
                          <p className="text-sm text-gray-600">${funnel.upsell.price}</p>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="flex flex-col items-center justify-center text-center h-full">
              <div className="w-16 h-16 bg-primary-100 rounded-full flex items-center justify-center mb-4">
                <Package className="w-8 h-8 text-primary-600" />
              </div>
              <p className="text-gray-500 text-sm">
                {dictionary["funnels-selected-home-label"]?.[language as Language] ?? "Acá aparecerán los embudos que selecciones"}
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}